import { memo, useEffect, useId, useLayoutEffect, useRef } from 'react'
import {
    CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
    FileOutlined, FileSyncOutlined, MinusCircleOutlined,
} from '@ant-design/icons'

import { Modal } from '../../ui/modal'
import { Button } from '../../ui/button'
import css from './ui.file-preparation.module.scss'

export type FilePreparationStatus = 'queued' | 'uploading' | 'processing' | 'downloading' | 'ready' | 'error' | 'skipped'
export type FilePreparationItem = {
    id: string
    name: string
    status: FilePreparationStatus
    uploadPercent?: number
    downloadPercent?: number
    resultCount?: number
    error?: string
}
export type FilePreparationProgress = {
    isOpen: boolean
    total: number
    completed: number
    phase: 'preparing' | 'saving' | 'complete'
    items: FilePreparationItem[]
    error?: string
}

const activeStatuses = new Set<FilePreparationStatus>(['uploading', 'processing', 'downloading'])
const percentage = (value?: number) => typeof value === 'number' && Number.isFinite(value)
    ? Math.min(100, Math.max(0, Math.floor(value))) : undefined
const statusLabel = (item: FilePreparationItem) => {
    switch (item.status) {
        case 'queued': return 'В очереди'
        case 'uploading': return percentage(item.uploadPercent) === undefined ? 'Загрузка на сервер' : `Загрузка · ${percentage(item.uploadPercent)}%`
        case 'processing': return 'Обрабатывается'
        case 'downloading': return percentage(item.downloadPercent) === undefined ? 'Получение результата' : `Получение · ${percentage(item.downloadPercent)}%`
        case 'ready': return 'Подготовлено'
        case 'error': return 'Ошибка'
        case 'skipped': return 'Пропущено'
    }
}
const FileStatusIcon = ({ status }: { status: FilePreparationStatus }) => {
    switch (status) {
        case 'ready': return <CheckCircleOutlined />
        case 'error': return <CloseCircleOutlined />
        case 'skipped': return <MinusCircleOutlined />
        case 'queued': return <ClockCircleOutlined />
        default: return <FileOutlined />
    }
}

const PreparationFile = memo(({ item }: { item: FilePreparationItem }) => {
    const active = activeStatuses.has(item.status)
    const transferPercent = item.status === 'uploading' ? percentage(item.uploadPercent)
        : item.status === 'downloading' ? percentage(item.downloadPercent) : undefined
    return <li className={`${css.file} ${css[item.status]}`} data-active={active || undefined}>
        <span className={css.fileIcon} aria-hidden='true'><FileStatusIcon status={item.status} /></span>
        <div className={css.fileDetails}>
            <strong className={css.fileName}>{item.name}</strong>
            {item.resultCount !== undefined && item.resultCount > 0 && <span className={css.resultCount}>Получено файлов: {item.resultCount}</span>}
            {item.error && <p className={css.fileError}>{item.error}</p>}
            {transferPercent !== undefined && <div
                className={css.transferTrack} role='progressbar'
                aria-label={`${item.status === 'uploading' ? 'Загрузка файла' : 'Получение файла'}: ${item.name}`}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={transferPercent}
            ><span style={{ width: `${transferPercent}%` }} /></div>}
        </div>
        <span className={css.fileStatus}>
            <span>{statusLabel(item)}</span>
            {active && <span className={css.spinner} aria-hidden='true' />}
        </span>
    </li>
})
PreparationFile.displayName = 'PreparationFile'

/** Progress reflects completed files and transferred bytes; processing has no fabricated percentage. */
export const ListenerFilePreparation = ({ progress, onClose }: { progress: FilePreparationProgress; onClose: () => void }) => {
    const titleID = useId(), bodyRef = useRef<HTMLDivElement>(null)
    const isOpen = progress.isOpen, complete = progress.phase === 'complete'
    const total = Number.isSafeInteger(progress.total) && progress.total >= 0 ? progress.total : progress.items.length
    const completed = Math.min(total, Math.max(0, Number.isFinite(progress.completed) ? Math.floor(progress.completed) : 0))
    const percent = total ? Math.floor(completed / total * 100) : 0
    const ready = progress.items.filter(item => item.status === 'ready').length
    const errors = progress.items.filter(item => item.status === 'error').length
    const skipped = progress.items.filter(item => item.status === 'skipped').length
    const hasErrors = errors > 0 || !!progress.error
    const current = progress.items.findIndex(item => activeStatuses.has(item.status))
    const activeID = current >= 0 ? progress.items[current].id : undefined
    const title = complete ? hasErrors ? 'Обработка завершена с ошибками' : 'Обработка завершена' : 'Обработка файлов'

    // Modal locks body scrolling in its passive effect. Capture the prior state
    // before that happens and restore it when hiding, including a card unmount.
    useLayoutEffect(() => {
        if (!isOpen) return
        const overflow = document.body.style.overflow
        const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        return () => {
            document.body.style.overflow = overflow
            if (focused?.isConnected) focused.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const trapFocus = (event: KeyboardEvent) => {
            const dialog = bodyRef.current?.closest<HTMLElement>('[role="dialog"]')
            if (!dialog) return
            // The shared Modal/Drawer Enter listeners must not affect the card
            // behind this dialog. Native button activation remains available.
            if (event.key === 'Enter') { event.stopPropagation(); return }
            if (event.key !== 'Tab') return
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
            const first = focusable[0], last = focusable[focusable.length - 1]
            if (!first) { event.preventDefault(); dialog.focus(); return }
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog || !dialog.contains(document.activeElement))) {
                event.preventDefault(); last.focus()
            } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
                event.preventDefault(); first.focus()
            }
        }
        document.addEventListener('keydown', trapFocus, true)
        return () => document.removeEventListener('keydown', trapFocus, true)
    }, [isOpen])

    useEffect(() => {
        if (isOpen) bodyRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' })
    }, [isOpen, activeID])

    if (!isOpen) return null
    return <Modal isOpen isEsc onCancel={onClose} maxWidth='640px' zIndex='1090' ariaLabelledBy={titleID} className={css.dialog}>
        <div ref={bodyRef} className={css.container}>
            <header className={css.header}>
                <span className={`${css.headingIcon} ${complete ? hasErrors ? css.errorIcon : css.successIcon : ''}`} aria-hidden='true'>
                    {complete ? hasErrors ? <CloseCircleOutlined /> : <CheckCircleOutlined /> : <FileSyncOutlined />}
                </span>
                <div className={css.heading}>
                    <span>Файлы слушателя</span>
                    <h4 id={titleID}>{title}</h4>
                </div>
            </header>

            <div className={css.body}>
                <section className={css.overall} aria-label='Подготовка файлов'>
                    <div className={css.overallHeading}>
                        <div>
                            <span className={css.preparationLabel}>Подготовка файлов</span>
                            <p className={css.processed} role='status' aria-live='polite'>Обработано {completed} из {total}</p>
                        </div>
                        <strong className={`${css.percent} ${complete && !hasErrors ? css.successText : ''}`}>{percent}<span>%</span></strong>
                    </div>
                    <div className={css.progressTrack} role='progressbar' aria-label='Прогресс подготовки файлов'
                        aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-valuetext={`Обработано ${completed} из ${total}`}>
                        <span className={complete && !hasErrors ? css.progressSuccess : undefined} style={{ width: `${percent}%` }} />
                    </div>
                    <div className={css.counters}>
                        <span className={css.readyCount}>Подготовлено: {ready}</span>
                        {errors > 0 && <span className={css.errorCount}>Ошибок: {errors}</span>}
                        {skipped > 0 && <span>Пропущено: {skipped}</span>}
                    </div>
                </section>

                {!complete && <p className={css.currentStage} role='status' aria-live='polite'>
                    <span>{progress.phase === 'saving' ? 'Добавление файлов в анкету…' : current >= 0 ? `Подготовка файла ${current + 1} из ${total}` : 'Подготовка файлов…'}</span>
                    <span className={css.spinner} aria-hidden='true' />
                </p>}

                {progress.items.length > 0 && <ul className={css.files} tabIndex={0} aria-label='Ход обработки файлов'>
                    {progress.items.map(item => <PreparationFile key={item.id} item={item} />)}
                </ul>}

                {progress.error && <p className={css.batchError} role='alert'>{progress.error}</p>}
                {complete && <p className={css.finishNote} role='status' aria-live='polite'>{hasErrors
                    ? 'Проверьте сообщения у файлов с ошибками.'
                    : 'Файлы обработаны. Можно продолжить работу с анкетой.'}</p>}
            </div>

            <footer className={css.footer}>
                <p>{complete ? 'Результат обработки показан для каждого файла.' : 'Окно можно свернуть — обработка продолжится.'}</p>
                <Button variant={complete ? 'primary' : 'primaryDark'} padding='0 22px' onClick={onClose}>{complete ? 'Закрыть' : 'Свернуть'}</Button>
            </footer>
        </div>
    </Modal>
}
